import { observer } from 'mobx-react-lite'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useCallback, useState } from 'react'
import { Avatar, AvatarGroup, Box, Fade, Icon, IconButton, Tooltip, Typography } from '@mui/material'
import { PageMetrics } from './PageMetrics'
import { CardSkillGapRating } from './CardSkillGapRating'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { useNavigate } from 'react-router-dom'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { Word } from 'src/feature/localization/component/atom/Word'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { iconMapping } from 'src/util/icon-mapping'
import FlagIcon from '@mui/icons-material/Flag'
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid'
import { TodoChecklist } from './TodoChecklist'
import { buildTodoChecklistItems, createTodoChecklistTemplates, getDisabledTodoActionTooltip } from './todo-checklist.helper'
import type { TeamSkillGapMetrics } from '../../model/my-team'
import { OrgLevelModal } from 'src/feature/control-panel/component/OrgLevelModal'

const CARD_FADE_TIMEOUT = 500
const AVATAR_SIZE = 25
const AVATAR_FONT_SIZE = 12
const AVATAR_GROUP_MAX = 3
const AVATAR_GROUP_SPACING = 10
const TEAM_MEMBERS_ICON_SIZE = 16
const TEAM_TITLE_MAX_WIDTH = '25em'

export type TeamListCardProps = {
  levelId: string
  levelName: string
  isMetricsLoading?: boolean
  teamMetrics?: TeamSkillGapMetrics
  onRoleCreated?: () => Promise<void> | void
}

export const TeamListCard = observer(({ levelId, levelName, isMetricsLoading, teamMetrics, onRoleCreated }: TeamListCardProps) => {
  const navigate = useNavigate()
  const { translation } = useLocalizationStore()
  const [isBackSideVisible, setIsBackSideVisible] = useState(false)
  const [isCreateRoleDialogOpen, setIsCreateRoleDialogOpen] = useState(false)

  const pageTranslation = translation.pages.myTeam
  const { rolesTable } = pageTranslation
  const disabledActionTooltip = getDisabledTodoActionTooltip(rolesTable.disabledActionTooltip())
  const teamSkillGap = Boolean(isMetricsLoading)
  const managers = teamMetrics?.managers ?? []
  const currentFocus = teamMetrics?.lowestRatedRole || '-'
  const currentFocusLabel = `${pageTranslation.metricsHeader.currentFocus()}: ${currentFocus}`
  const allCompleted = teamMetrics?.allCompleted ?? false

  const handleOpenCreateRoleDialog = useCallback(() => {
    setIsCreateRoleDialogOpen(true)
  }, [])

  const handleCloseCreateRoleDialog = useCallback(async (reload: boolean) => {
    setIsCreateRoleDialogOpen(false)

    if (!reload) {
      return
    }

    await onRoleCreated?.()
  }, [onRoleCreated])

  const createRoleDialogProps = {
    show: isCreateRoleDialogOpen,
    onClose: handleCloseCreateRoleDialog,
    editLevel: null,
    parentId: levelId,
    lockedCreateType: 'role',
  } as const

  const checklistTemplates = createTodoChecklistTemplates({
    levelId,
    levelName,
    includeRoles: true,
    labels: {
      roles: {
        completed: rolesTable.rolesTodoCompleted(),
        pending: rolesTable.rolesTodoPending(),
        action: rolesTable.rolesTodoAction(),
      },
      skillSet: {
        completed: rolesTable.skillSetTodoCompleted(),
        pending: rolesTable.skillSetTodoPending(),
        action: rolesTable.skillSetTodoAction(),
      },
      expectedRating: {
        completed: rolesTable.expectedRatingTodoCompleted(),
        pending: rolesTable.expectedRatingTodoPending(),
        action: rolesTable.expectedRatingTodoAction(),
      },
      users: {
        completed: rolesTable.usersTodoCompleted(),
        pending: rolesTable.usersTodoPending(),
        action: rolesTable.usersTodoAction(),
      },
    },
  })
  const checklistItems = buildTodoChecklistItems({
    templates: checklistTemplates,
    completionMap: teamMetrics,
    allCompleted,
    overrides: {
      rolesCompleted: allCompleted ? undefined : { onAction: handleOpenCreateRoleDialog },
      usersCompleted: {
        actionLabel: rolesTable.skillSetTodoAction(),
        to: appRoutePath.myTeamRoles(levelId),
        state: { levelName },
      },
    },
  })

  const handleShowMoreClick = useCallback(() => {
    navigate(appRoutePath.myTeamRoles(levelId), { state: { levelName } })
  }, [navigate, levelId, levelName])

  const handleToggleSide = useCallback(() => {
    setIsBackSideVisible(current => !current)
  }, [])

  const getManagerInitials = (managerName: string) => {
    const initials = managerName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() ?? '')
      .join('')

    return initials || managerName.slice(0, 2).toUpperCase()
  }

  return (
    <Fade in timeout={CARD_FADE_TIMEOUT} mountOnEnter unmountOnExit>
      <AppPaper sx={styles.root}>
        {!allCompleted && (
          <Tooltip title={pageTranslation.metricsHeader.flagTooltip()} arrow>
            <IconButton
              aria-label={isBackSideVisible ? pageTranslation.metricsHeader.showMetricsButtonLabel() : pageTranslation.metricsHeader.showTodoListButtonLabel()}
              onClick={handleToggleSide}
              sx={{
                ...styles.flipButton,
                backgroundColor: isBackSideVisible ? ColorTokens.primary10 : ColorTokens.red10,
                color: isBackSideVisible ? ColorTokens.primary500 : ColorTokens.red200,
              }}
              size='small'
            >
              {isBackSideVisible ? <FlipCameraAndroidIcon fontSize='small' /> : <FlagIcon fontSize='small' />}
            </IconButton>
          </Tooltip>
        )}
        <Box sx={styles.scene}>
          <Box
            sx={{
              ...styles.flipper,
              transform: isBackSideVisible ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            <Box sx={styles.face}>
              <AppBox width='100%' alignItems='center' justifyContent='space-between' gap={5} sx={{ minWidth: 0 }}>
                <AppBox flexDirection='column' alignItems='flex-start' justifyContent='center' gap={5} sx={{ minWidth: 0 }}>
                  <Tooltip title={levelName} placement='top' arrow>
                    <Typography variant='h4' sx={styles.teamName} noWrap>{levelName}</Typography>
                  </Tooltip>
                  <Typography sx={styles.teamUsersRow}>
                    <Icon
                      sx={{
                        transition: 'color 0.3s ease',
                        height: TEAM_MEMBERS_ICON_SIZE,
                        width: TEAM_MEMBERS_ICON_SIZE,
                        color: 'text.secondary',
                      }}
                      component={iconMapping['PeopleAltIcon']}
                    />
                    {teamMetrics?.users || 0} {pageTranslation.metricsHeader.users()}
                  </Typography>
                </AppBox>
                <Tooltip
                  arrow
                  placement='top'
                  title={
                    <Box sx={styles.managerTooltip}>
                      <Typography sx={{ ...styles.managerTooltipText, color: ColorTokens.green200 }}>{pageTranslation.metricsHeader.managers()}</Typography>
                      {managers.length > 0
                        ? managers.map(manager => (
                          <Typography key={manager} sx={styles.managerTooltipText}>
                            {manager}
                          </Typography>
                        ))
                        : <Typography sx={styles.managerTooltipText}>-</Typography>}
                    </Box>
                  }
                >
                  <AvatarGroup
                    max={AVATAR_GROUP_MAX}
                    spacing={AVATAR_GROUP_SPACING}
                    sx={{ justifyContent: 'flex-end', flexShrink: 0 }}
                    slotProps={{
                      additionalAvatar: {
                        sx: styles.managerAvatar,
                      },
                    }}
                  >
                    {managers.map(manager => (
                      <Avatar
                        key={manager}
                        sx={{
                          ...styles.managerAvatar,
                          width: AVATAR_SIZE,
                          height: AVATAR_SIZE,
                        }}
                      >
                        {getManagerInitials(manager)}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </Tooltip>
              </AppBox>
              <CardSkillGapRating
                value={teamMetrics?.avgSkillRating || 0}
                expectedValue={teamMetrics?.avgExpectedSkillRating || 0}
                leading={teamMetrics?.highestRatedRole || '-'}
                isLoading={teamSkillGap}
              />
              <PageMetrics
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 15,
                }}
                metrics={[
                  { label: pageTranslation.metricsHeader.avgGap(), value: teamMetrics?.avgGap || '0.0' },
                  { label: pageTranslation.metricsHeader.avgRating(), value: teamMetrics?.avgSkillRating || '0.0' },
                  {
                    label: pageTranslation.metricsHeader.coverage(),
                    value: `${teamMetrics?.coverage || 0}%`,
                    valueColor: true,
                    tootlTipDesc: pageTranslation.metricsHeader.coverageTooltip(),
                  },
                ]}
                backgroundColor={ColorTokens.primary10}
                isLoading={teamSkillGap}
              />
              <AppDivider sx={{ marginBottom: -13 }} />
              <AppBox alignItems='center' justifyContent='space-between' width='100%' sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ ...styles.title, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={currentFocusLabel}
                >
                  {pageTranslation.metricsHeader.currentFocus()}: <Word>{currentFocus}</Word>
                </Typography>
                <AppBox alignItems='center' gap={3} sx={{ minWidth: 0 }}>
                  <Word sx={{ fontSize: 10, color: 'secondary.main' }}>{pageTranslation.viewAllRolesButton()}</Word>
                  <Tooltip
                    title={pageTranslation.viewAllRolesButton()}
                    arrow
                  >
                    <IconButton
                      size='small'
                      sx={styles.expandButton}
                      onClick={handleShowMoreClick}
                    >
                      <ChevronRightIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </AppBox>
              </AppBox>
            </Box>
            <Box sx={{ ...styles.face, ...styles.backFace }}>
              <TodoChecklist
                title={rolesTable.todoListTitle()}
                titleSx={styles.backTitle}
                listSx={styles.todoList}
                contentSx={styles.todoContent}
                items={checklistItems}
                allCompleted={allCompleted}
                showCheckboxWhenAllCompleted={false}
                enforceSequentialActions
                disabledActionTooltip={disabledActionTooltip}
              />
            </Box>
          </Box>
        </Box>
        {isCreateRoleDialogOpen && (
          <OrgLevelModal {...createRoleDialogProps} />
        )}
      </AppPaper>
    </Fade>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    position: 'relative',
    gap: 15,
    padding: '10px 15px',
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: '10px',
    transition: 'border-color 0.2s ease-in-out',
    perspective: '1200px',
    overflow: 'hidden',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  scene: {
    width: '100%',
  },
  flipper: {
    width: '100%',
    position: 'relative',
    transformStyle: 'preserve-3d',
    transition: 'transform 0.6s ease',
  },
  face: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 15,
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  backFace: {
    position: 'absolute',
    inset: 0,
    transform: 'rotateY(180deg)',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    width: '100%',
    overflowY: 'auto',
    paddingBottom: 2,
  },
  flipButton: {
    position: 'absolute',
    top: 8,
    right: 20,
    transform: 'none',
    zIndex: 1,
  },
  title: {
    color: 'text.secondary',
    fontSize: 12,
  },
  teamName: {
    maxWidth: TEAM_TITLE_MAX_WIDTH,
    alignSelf: 'start',
  },
  teamUsersRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 11,
    color: 'text.secondary',
    gap: 5,
  },
  managerAvatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    fontSize: AVATAR_FONT_SIZE,
    fontWeight: 700,
    backgroundColor: 'primary.main',
    color: 'background.default',
    border: 0,
  },
  managerTooltip: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    padding: '2px 0',
  },
  managerTooltipText: {
    color: 'common.white',
    fontSize: 11,
    lineHeight: 1.4,
  },
  expandButton: {
    color: 'text.secondary',
    backgroundColor: 'background.dark',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'translateX(1px)',
    },
  },
  backTitle: {
    alignSelf: 'flex-start',
  },
  todoList: {
    width: '100%',
  },
  todoContent: {
    flex: 1,
  },
}
